"use client";

import type { RefObject } from "react";
import {
  CopyButton,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { BLAND_ALTMAN_PLOT_MAKER_STRINGS } from "@/components/tools/bland-altman-plot-maker";
import {
  CHART_AMBER,
  CHART_BLUE,
  CHART_GREEN,
  CHART_INK,
  CHART_RED,
  Chart,
  ChartDownloads,
} from "@/components/tools/stats-clinical/chart";
import {
  DataGrid,
  WorkingLine,
} from "@/components/tools/stats-clinical/data-grid";
import { t } from "@/lib/i18n/interpolate";
import {
  type AgreementResult,
  type AgreementScale,
  type Interval,
  judgeAgreement,
  regressionLimitsAt,
} from "@/lib/tool-kit/stats-clinical/bland-altman";
import {
  type Formatter,
  fixed,
  flexible,
  formatP,
} from "@/lib/tool-kit/stats-research/format";

/*
 * Plot and result panel of the Bland–Altman plot maker. Strings are the widget's own.
 */

type Strings = typeof BLAND_ALTMAN_PLOT_MAKER_STRINGS;

export interface PlotOptions {
  bands: boolean;
  trend: boolean;
  regressionLimits: boolean;
  acceptable: number | null;
}

export interface Names {
  a: string;
  b: string;
  unit: string;
}

/** Decimals that give about three significant figures of the SD, as Bland & Altman print. */
function decimalsFor(result: AgreementResult): number {
  if (result.scale === "ratio") {
    return 3;
  }
  const places = 2 - Math.floor(Math.log10(result.sd));
  return Math.min(6, Math.max(0, places));
}

function num(fmt: Formatter, digits: number, value: number): string {
  return fixed(fmt, value, digits);
}

function span(fmt: Formatter, digits: number, interval: Interval): string {
  return `${num(fmt, digits, interval.lower)} – ${num(fmt, digits, interval.upper)}`;
}

function yAxisLabel(
  strings: Strings,
  scale: AgreementScale,
  names: Names
): string {
  const key = {
    difference: "yDifference",
    percent: "yPercent",
    ratio: "yRatio",
  } as const;
  return t(strings[key[scale]], { a: names.a, b: names.b });
}

function unitSuffix(scale: AgreementScale, names: Names): string {
  if (scale === "percent") {
    return "%";
  }
  return scale === "difference" && names.unit ? ` ${names.unit}` : "";
}

function xAxisLabel(strings: Strings, names: Names): string {
  const base = t(strings.xMean, { a: names.a, b: names.b });
  return names.unit ? `${base} (${names.unit})` : base;
}

export function BlandAltmanPlot({
  result,
  names,
  options,
  strings,
  fmt,
  svgRef,
}: {
  result: AgreementResult;
  names: Names;
  options: PlotOptions;
  strings: Strings;
  fmt: Formatter;
  svgRef: RefObject<SVGSVGElement | null>;
}) {
  const xs = result.points.map((p) => p.x);
  const ys = result.points.map((p) => p.y);
  const accept = options.acceptable;
  const yValues = [
    ...ys,
    ...(options.bands
      ? [result.upperCi.upper, result.lowerCi.lower]
      : [result.upper, result.lower]),
    ...(accept === null ? [] : [accept, -accept]),
  ];
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const label = (value: number) => num(fmt, decimalsFor(result), value);
  return (
    <Chart
      formatX={(v) => flexible(fmt, v, 2)}
      formatY={(v) => flexible(fmt, v, 3)}
      idPrefix="ba"
      label={t(strings.plotLabel, { n: fmt(result.n) })}
      svgRef={svgRef}
      xDomain={[xMin, xMax]}
      xLabel={xAxisLabel(strings, names)}
      yDomain={[Math.min(...yValues), Math.max(...yValues)]}
      yLabel={yAxisLabel(strings, result.scale, names)}
    >
      {(area) => {
        const band = (interval: Interval, colour: string, key: string) =>
          options.bands ? (
            <rect
              fill={colour}
              height={Math.abs(area.y(interval.lower) - area.y(interval.upper))}
              key={key}
              opacity={0.14}
              width={area.right - area.left}
              x={area.left}
              y={Math.min(area.y(interval.lower), area.y(interval.upper))}
            />
          ) : null;
        const line = (
          value: number,
          colour: string,
          dash: string | undefined,
          text: string
        ) => (
          <g key={text}>
            <line
              stroke={colour}
              strokeDasharray={dash}
              strokeWidth={1.5}
              x1={area.left}
              x2={area.right}
              y1={area.y(value)}
              y2={area.y(value)}
            />
            <text
              fill={colour}
              fontFamily="system-ui, sans-serif"
              fontSize={11}
              textAnchor="end"
              x={area.right - 6}
              y={area.y(value) - 5}
            >
              {text}
            </text>
          </g>
        );
        const regression = result.regressionLimits;
        const [x0, x1] = [xMin, xMax];
        return (
          <>
            {band(result.upperCi, CHART_RED, "bu")}
            {band(result.lowerCi, CHART_RED, "bl")}
            {band(result.biasCi, CHART_BLUE, "bb")}
            {accept === null
              ? null
              : [accept, -accept].map((value) => (
                  <line
                    key={`a${value}`}
                    stroke={CHART_INK}
                    strokeDasharray="2 4"
                    x1={area.left}
                    x2={area.right}
                    y1={area.y(value)}
                    y2={area.y(value)}
                  />
                ))}
            {line(
              result.bias,
              CHART_BLUE,
              undefined,
              t(strings.meanLine, { value: label(result.bias) })
            )}
            {line(
              result.upper,
              CHART_RED,
              "6 4",
              t(strings.upperLine, { value: label(result.upper) })
            )}
            {line(
              result.lower,
              CHART_RED,
              "6 4",
              t(strings.lowerLine, { value: label(result.lower) })
            )}
            {options.trend && result.trend ? (
              <line
                stroke={CHART_GREEN}
                strokeWidth={1.5}
                x1={area.x(x0)}
                x2={area.x(x1)}
                y1={area.y(result.trend.intercept + result.trend.slope * x0)}
                y2={area.y(result.trend.intercept + result.trend.slope * x1)}
              />
            ) : null}
            {options.regressionLimits && regression
              ? [0, 1].map((side) => (
                  <line
                    key={`r${side}`}
                    stroke={CHART_AMBER}
                    strokeDasharray="3 3"
                    strokeWidth={1.5}
                    x1={area.x(x0)}
                    x2={area.x(x1)}
                    y1={area.y(regressionLimitsAt(regression, x0)[side])}
                    y2={area.y(regressionLimitsAt(regression, x1)[side])}
                  />
                ))
              : null}
            {result.points.map((p, i) => (
              <circle
                cx={area.x(p.x)}
                cy={area.y(p.y)}
                fill={CHART_BLUE}
                fillOpacity={0.65}
                // biome-ignore lint/suspicious/noArrayIndexKey: points have no identity
                key={i}
                r={4}
                stroke="#ffffff"
                strokeWidth={0.8}
              />
            ))}
          </>
        );
      }}
    </Chart>
  );
}

function verdictText(
  strings: Strings,
  result: AgreementResult,
  accept: number,
  fmt: Formatter
): string {
  const verdict = judgeAgreement(result, accept);
  const key = {
    within: "verdictWithin",
    uncertain: "verdictUncertain",
    outside: "verdictOutside",
  } as const;
  return t(strings[key[verdict]], { limit: flexible(fmt, accept, 3) });
}

export function ResultsPanel({
  result,
  names,
  options,
  strings,
  fmt,
  svgRef,
}: {
  result: AgreementResult;
  names: Names;
  options: PlotOptions;
  strings: Strings;
  fmt: Formatter;
  svgRef: RefObject<SVGSVGElement | null>;
}) {
  const { scale, trend, ratio } = result;
  const digits = decimalsFor(result);
  const n = (value: number) => num(fmt, digits, value);
  const pair = ratio
    ? {
        bias: ratio.bias,
        upper: ratio.upper,
        lower: ratio.lower,
        biasCi: ratio.biasCi,
        upperCi: ratio.upperCi,
        lowerCi: ratio.lowerCi,
      }
    : result;
  const shown = (value: number) => n(value);
  const unit = unitSuffix(scale, names);
  const report = t(strings.report, {
    n: fmt(result.n),
    bias: shown(pair.bias),
    biasLow: shown(pair.biasCi.lower),
    biasHigh: shown(pair.biasCi.upper),
    lower: shown(pair.lower),
    upper: shown(pair.upper),
  });
  const rows: string[][] = [
    [
      ratio ? strings.rowRatio : strings.rowBias,
      shown(pair.bias),
      span(fmt, digits, pair.biasCi),
    ],
    [
      ratio ? strings.rowLowerRatio : strings.rowLower,
      shown(pair.lower),
      span(fmt, digits, pair.lowerCi),
    ],
    [
      ratio ? strings.rowUpperRatio : strings.rowUpper,
      shown(pair.upper),
      span(fmt, digits, pair.upperCi),
    ],
    [strings.rowSd, n(result.sd), ""],
  ];
  if (trend) {
    rows.push([
      strings.rowSlope,
      flexible(fmt, trend.slope, 4),
      `${flexible(fmt, trend.slopeLower, 4)} – ${flexible(fmt, trend.slopeUpper, 4)}`,
    ]);
  }
  return (
    <div aria-live="polite" className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <BlandAltmanPlot
        fmt={fmt}
        names={names}
        options={options}
        result={result}
        strings={strings}
        svgRef={svgRef}
      />
      <ChartDownloads
        baseName="bland-altman-plot"
        pngLabel={strings.downloadPng}
        svgLabel={strings.downloadSvg}
        svgRef={svgRef}
      />
      <StatGrid>
        <StatCard
          emphasis
          label={ratio ? strings.rowRatio : strings.rowBias}
          value={`${shown(pair.bias)}${unit}`}
        />
        <StatCard
          label={ratio ? strings.rowLowerRatio : strings.rowLower}
          value={`${shown(pair.lower)}${unit}`}
        />
        <StatCard
          label={ratio ? strings.rowUpperRatio : strings.rowUpper}
          value={`${shown(pair.upper)}${unit}`}
        />
      </StatGrid>
      <p className="text-foreground text-sm leading-relaxed">
        {t(strings[ratio ? "readingRatio" : "reading"], {
          a: names.a,
          b: names.b,
          bias: `${shown(pair.bias)}${unit}`,
          biasLow: shown(pair.biasCi.lower),
          biasHigh: shown(pair.biasCi.upper),
          lower: shown(pair.lower),
          upper: shown(pair.upper),
          outside: fmt(result.outside),
          n: fmt(result.n),
        })}
      </p>
      {options.acceptable !== null && scale !== "ratio" ? (
        <p className="text-foreground text-sm leading-relaxed">
          {verdictText(strings, result, options.acceptable, fmt)}
        </p>
      ) : null}
      {trend ? (
        <p className="text-foreground text-sm leading-relaxed">
          {t(trend.slopeP < 0.05 ? strings.trendYes : strings.trendNo, {
            slope: flexible(fmt, trend.slope, 4),
            p: formatP(fmt, trend.slopeP),
          })}
        </p>
      ) : null}
      <DataGrid
        head={[
          strings.columnMeasure,
          strings.columnEstimate,
          strings.columnInterval,
        ]}
        rows={rows}
      />
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={report}
        />
        <span className="font-mono text-muted-foreground text-xs">
          {report}
        </span>
      </div>
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
        <WorkingLine>
          {t(strings.workingBias, {
            bias: flexible(fmt, result.bias, 4),
            sd: flexible(fmt, result.sd, 4),
            n: fmt(result.n),
          })}
        </WorkingLine>
        <WorkingLine>
          {t(strings.workingLimits, {
            lower: flexible(fmt, result.lower, 4),
            upper: flexible(fmt, result.upper, 4),
          })}
        </WorkingLine>
        <WorkingLine>
          {t(strings.workingCi, {
            t: flexible(fmt, result.tCritical, 4),
            df: fmt(result.n - 1),
          })}
        </WorkingLine>
        <p className="text-muted-foreground text-sm">{strings.workingNote}</p>
      </div>
    </div>
  );
}
